// src/utils/tableTheme.js
import axiosInstance from "../axiosInstance";

/** Clé LS & event interne */
const LS_KEY = "prefs:tableTheme";

/** Defaults (cohérents Tailwind) */
export const DEFAULT_TABLE_THEME = {
  headerBg: "#F3F4F6",  // gris clair (th)
  headerText: "#0F172A", // slate-900 (th)
  vLine: "#E5E7EB",      // gris bordure verticale
};

/* ---------------- Utils internes ---------------- */
const safeParseJson = (s) => {
  try { return JSON.parse(s); } catch { return null; }
};

const isValidCssColor = (c) => {
  if (!c || typeof c !== "string") return false;
  const el = document.createElement("span");
  el.style.color = "";
  el.style.color = c.trim();
  return el.style.color !== "";
};

const sanitizeTheme = (maybe) => {
  const t = (maybe && typeof maybe === "object") ? maybe : {};
  const out = { ...DEFAULT_TABLE_THEME };

  if (isValidCssColor(t.headerBg)) out.headerBg = t.headerBg.trim();
  if (isValidCssColor(t.headerText)) out.headerText = t.headerText.trim();
  if (isValidCssColor(t.vLine)) out.vLine = t.vLine.trim();

  return out;
};

/* ---------------- Lecture / Écriture locale ---------------- */

/** Charge le thème depuis localStorage (fallback sur DEFAULT_TABLE_THEME). */
export function loadTableTheme() {
  const raw = localStorage.getItem(LS_KEY);
  if (!raw) return { ...DEFAULT_TABLE_THEME };
  const merged = { ...DEFAULT_TABLE_THEME, ...(safeParseJson(raw) || {}) };
  return sanitizeTheme(merged);
}

/** Renvoie un objet `style={...}` prêt à être posé sur un conteneur React. */
export function themeStyleVars(theme) {
  const t = sanitizeTheme(theme || loadTableTheme());
  return {
    "--doc-th-bg": t.headerBg,
    "--doc-th-text": t.headerText,
    "--doc-vline": t.vLine,
  };
}

/** Applique les variables CSS sur un élément (par défaut : documentElement). */
export function applyThemeToElement(el = document.documentElement, theme) {
  const vars = themeStyleVars(theme);
  Object.entries(vars).forEach(([k, v]) => el.style.setProperty(k, v));
}

/* ---------------- Sync BACKEND ---------------- */

/**
 * Sauvegarde le thème :
 *  - nettoie/merge avec les defaults,
 *  - persiste en localStorage,
 *  - notifie les autres onglets,
 *  - tente un PATCH côté back sur `societe.table_theme`.
 *
 * Endpoint attendu côté back :
 *   PATCH /entreprise/table-theme
 *   body: { table_theme: { headerBg, headerText, vLine } }
 */
export async function saveTableTheme(next) {
  const merged = sanitizeTheme({ ...DEFAULT_TABLE_THEME, ...(next || {}) });

  // LocalStorage
  localStorage.setItem(LS_KEY, JSON.stringify(merged));
  // notification interne (autres onglets / composants)
  localStorage.setItem("docs:refresh", String(Date.now()));

  // Application immédiate sur l'UI courante
  try {
    applyThemeToElement(document.documentElement, merged);
  } catch { /* pas grave en SSR */ }

  // Sauvegarde en base (best effort)
  try {
    await axiosInstance.patch("/entreprise/table-theme", { table_theme: merged });
  } catch {
    // on ne casse pas l'UI si l'API n'est pas prête
  }

  return merged;
}

/**
 * À appeler quand on récupère la société (GET /entreprise).
 * Lit `societe.table_theme` (JSON ou string JSON),
 * merge avec les defaults, applique le thème et met le LS à jour.
 */
export function initTableThemeFromSociete(societe) {
  if (!societe) {
    const local = loadTableTheme();
    applyThemeToElement(document.documentElement, local);
    return local;
  }

  let raw = societe.table_theme ?? societe.tableTheme ?? null;
  if (typeof raw === "string") {
    raw = safeParseJson(raw) || null;
  }

  const merged = sanitizeTheme({ ...DEFAULT_TABLE_THEME, ...(raw || {}) });

  try {
    localStorage.setItem(LS_KEY, JSON.stringify(merged));
    localStorage.setItem("docs:refresh", String(Date.now()));
  } catch { /* no-op */ }

  try {
    applyThemeToElement(document.documentElement, merged);
  } catch { /* no-op */ }

  return merged;
}

/* ---------------- Abonnement changements (multi-onglets) ---------------- */

/**
 * Abonne un callback aux changements de thème (storage + refresh local).
 * Retourne une fonction de cleanup.
 */
export function onThemeChange(cb) {
  if (typeof cb !== "function") return () => {};
  const handler = (e) => {
    if (e.key === LS_KEY || e.key === "docs:refresh") {
      cb(loadTableTheme());
    }
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
